import { useCallback, useEffect, useState, type MouseEvent } from "react";

import { useDocPermissions } from "@/components/access/use-doc-permissions";
import { cn } from "@/lib/cn";
import { useAppDispatch } from "@store";
import { editEntity } from "@store/story";
import classNames from "classnames";
import { styled } from "styled-components";
import { measureResizeControls } from "../measure-resize-controls";
import styles from "./ResizeAxis.module.scss";
import type { ResizeAxisProps } from "./types";

export const ResizeAxis = ({
  heightProps,
  triggerInitialHeight,
  widthProps,
  callbackFunc,
}: ResizeAxisProps) => {
  const dispatch = useAppDispatch();
  const [tooltip, setTooltip] = useState<boolean>(false);
  // Send flow: viewers/commenters get no resize handles (flag off = everyone).
  const { canEdit } = useDocPermissions();

  useEffect(() => {
    if (triggerInitialHeight && canEdit) {
      dispatch(editEntity({ data: { height: heightProps.height } }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerInitialHeight]);

  const handler = (mouseDownEvent: MouseEvent<HTMLButtonElement>) => {
    const isOpposite: boolean =
      mouseDownEvent.currentTarget.classList.contains("opposite");
    const isHeight: boolean =
      mouseDownEvent.currentTarget.classList.contains("height");
    if (!isHeight) return handleWidth(mouseDownEvent, isOpposite);
    else return handleHeight(mouseDownEvent, isOpposite);
  };

  const handleWidth = useCallback(
    (mouseDownEvent: MouseEvent<HTMLButtonElement>, isOpposite: boolean) => {
      const { minWidthRef, max, min, emitWidth, property } = widthProps;
      // The props hold a one-shot, mount-time measurement that reads 0 for an
      // element mounted inside a hidden Tabs pane. Fall back to the live DOM
      // so the drag starts from the real size instead of snapping to a clamp.
      // The two numbers form a ratio, so they always come from the same source.
      const { containerWidth, elementDomWidth } =
        widthProps.containerWidth && widthProps.elementDomWidth
          ? {
              containerWidth: widthProps.containerWidth,
              elementDomWidth: widthProps.elementDomWidth,
            }
          : measureResizeControls(mouseDownEvent.currentTarget);
      // Nothing to scale against: refusing the drag beats jumping to 10%/100%.
      if (!containerWidth || !elementDomWidth) return;

      document.body.classList.add("--editor-select-disabled");
      setTooltip(true);
      if (callbackFunc) callbackFunc({ counter: false });
      let updated: string;
      const startSize: number = Number(containerWidth || 0);
      const startPosition: number = mouseDownEvent.clientX;

      function onMouseMove(mouseMoveEvent: { clientX: number }): void {
        let width = isOpposite
          ? startSize + startPosition - mouseMoveEvent.clientX
          : startSize - startPosition + mouseMoveEvent.clientX;

        if (minWidthRef?.current != null) {
          if (width < minWidthRef.current) {
            width = minWidthRef.current;
          }
        }

        const changed = Math.round((width / elementDomWidth) * max);
        updated = String(changed >= max ? max : changed <= min ? min : changed);
        emitWidth(updated);
      }

      function onMouseUp() {
        document.body.removeEventListener("mousemove", onMouseMove);
        if (updated !== undefined) {
          dispatch(editEntity({ data: { [property]: updated } }));
        }
        setTimeout(() => {
          setTooltip(false);
          if (callbackFunc) callbackFunc({ counter: true });
        }, 500);
        setTimeout(
          () => document.body.classList.remove("--editor-select-disabled"),
          0,
        );
      }

      document.body.addEventListener("mousemove", onMouseMove);
      document.documentElement.addEventListener("mouseup", onMouseUp, {
        once: true,
      });

      return () => {
        document.body.removeEventListener("mousemove", onMouseMove);
        document.documentElement.removeEventListener("mouseup", onMouseUp);
      };
    },
    [widthProps, dispatch, callbackFunc],
  );

  const handleHeight = (mouseDownEvent: MouseEvent<HTMLButtonElement>, isOpposite: boolean) => {
      document.body.classList.add("--editor-select-disabled");
      let height: number;
      const { containerHeight, emitHeight, property, max, min } = heightProps;
      const startSize: number = containerHeight || 0;
      const startPosition: number = mouseDownEvent.clientY;

      function onMouseMove(mouseMoveEvent: { clientY: number }): void {
        height = isOpposite
          ? startSize + startPosition - mouseMoveEvent.clientY
          : startSize - startPosition + mouseMoveEvent.clientY;
        if (max !== undefined && min !== undefined)
          height = height >= max ? max : height <= min ? min : height;
        emitHeight(height);
      }

      function onMouseUp() {
        document.body.removeEventListener("mousemove", onMouseMove);
        if (height !== undefined) {
          dispatch(editEntity({ data: { [property]: height } }));
        }
        setTimeout(
          () => document.body.classList.remove("--editor-select-disabled"),
          0,
        );
      }

      document.body.addEventListener("mousemove", onMouseMove);
      document.body.addEventListener("mouseup", onMouseUp, { once: true });

      return () => {
        document.body.removeEventListener("mousemove", onMouseMove);
        document.body.removeEventListener("mouseup", onMouseUp);
      };
    };

  if (!canEdit) return null;

  return (
    <ResizeStyled
      $width={widthProps.width}
      className={`${styles["resize-axis"]} resize-controls`}
      data-no-dnd
    >
      {Array.from({ length: 4 }, (_, i) => i).map((index: number) => (
        <button
          type="button"
          key={index}
          className={cn("", {
            opposite: [0, 3].includes(index),
            height: [0, 2].includes(index),
          })}
          onMouseDown={handler}
        ></button>
      ))}
      <div
        className={classNames("counter", {
          [styles["active"]]: tooltip,
          [styles["resize-counter"]]: true,
        })}
      >
        {widthProps.width}%
      </div>
    </ResizeStyled>
  );
};

const ResizeStyled = styled.div.attrs<{ $width: string }>(({ $width }) => ({
  style: {
    width: `${$width || "100"}%`,
  },
}))``;
